/**
 * SwagButton — Swagbucks cash-back / coupon helper
 *
 * A single WAR entry exposes `assets/icons/*`, `assets/images/*`, `assets/fonts/*`,
 * `assets/redesign/*` and `worker/injections/*` to every http(s) origin with no
 * `use_dynamic_url` — so its icon set and injected coupon UI are all fetchable from
 * any page at a fixed URL.
 *
 * The 128px icon is the cleanest signal: an image loads through `probeImage` with no
 * CORS grant, so it survives a restrictive page CSP.
 *
 * Manifest verified against version 400.12.6.3848.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'gngocbkfmikdgphklgmmehbjjlfgdemm';

export const swagbutton: ExtensionDefinition = {
  id: ID,
  name: 'SwagButton',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 300_000,
  notes:
    'Exposes its icons and worker/injections UI to every http(s) origin with no dynamic ' +
    'URL. The icon loads without a CORS grant; detection is conclusive.',
  signals: [
    {
      id: 'war-icon-128',
      kind: 'web-accessible-resource',
      description:
        'Load assets/icons/128.png via <img> — the toolbar icon exposed to every ' +
        'http(s) origin; needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'assets/icons/128.png'),
    },
    {
      id: 'war-injections-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch worker/injections/injections.js — the coupon-injection script, a vendor ' +
        'path independent of the icons.',
      weight: 0.5,
      run: () => probeFetch(ID, 'worker/injections/injections.js'),
    },
    {
      id: 'war-notification-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch worker/injections/notification.css — the injected notification stylesheet, ' +
        'a third independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'worker/injections/notification.css'),
    },
  ],
};
